import type { ReactNode } from 'react'
import type { ScanBarState } from '../shell/scanFocus'
import { StationShell } from '../ui/StationShell'
import type { CameraState } from '../ui/ScanBar'

/**
 * Scan every unit into the parcel, watch the ring drain, then weigh.
 *
 * The slab warning is the one piece of commercial judgement on this screen: a
 * parcel sitting just under a carrier weight slab is worth keeping in that box,
 * and a packer cannot be expected to hold the slab table in their head.
 */
export interface PackingProps {
  scan: ScanBarState
  camera?: CameraState
  /** Opaque - never the order number, never the customer (SEC-09). */
  orderRef: string
  remaining: Array<{ unitId: string; title: string }>
  scanned: Array<{ unitId: string; title: string }>
  boxRecommendation: string
  computedWeightGrams: number
  pendingDrafts?: number
  online?: boolean
  /** The loading, error or empty verdict, rendered above the work rather than
   *  instead of it - a failed refresh must not take the scan bar with it. */
  notice?: ReactNode
}

const SLAB_GRAMS = 500
/** Close enough that the next box would cost a whole slab more. */
const SLAB_WARN_WINDOW = 60

export function slabWarning(weightGrams: number): string | null {
  const headroom = SLAB_GRAMS - (weightGrams % SLAB_GRAMS)
  if (headroom > SLAB_WARN_WINDOW || headroom === SLAB_GRAMS) return null
  return `${headroom}g under the ${SLAB_GRAMS}g slab - keep this box`
}

export function Packing({
  scan,
  camera,
  orderRef,
  remaining,
  scanned,
  boxRecommendation,
  computedWeightGrams,
  pendingDrafts = 0,
  online = true,
  notice,
}: PackingProps) {
  const total = remaining.length + scanned.length
  const warning = slabWarning(computedWeightGrams)

  return (
    <StationShell
      title="Pack"
      progress={`${scanned.length} of ${total} in the box`}
      queue={remaining.length}
      scan={scan}
      camera={camera}
      pendingDrafts={pendingDrafts}
      online={online}
    >
      {notice}
      <p className="t-scan order-ref">{orderRef}</p>

      <div className="ring progress-drain" role="img" aria-label={`${scanned.length} of ${total} packed`}>
        <span className="count-tick t-display">{scanned.length}</span>
        <span className="muted">of {total}</span>
      </div>

      <ul className="row-list">
        {remaining.map((r) => (
          <li key={r.unitId} className="row">
            <span>{r.title}</span>
          </li>
        ))}
        {scanned.map((s) => (
          <li key={s.unitId} className="row row-checked">
            <span>{s.title}</span>
          </li>
        ))}
      </ul>

      <section className="card">
        <span className="chip">{boxRecommendation}</span>
        <span className="t-scan">{computedWeightGrams}g</span>
        {warning ? <p className="slab-warning">{warning}</p> : null}
      </section>
    </StationShell>
  )
}
